import {
  HTMLProps,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  arrow,
} from '@floating-ui/react'

import type { Placement, Side } from '@floating-ui/react'
import classNames from 'classnames'
import { WithChildren } from '@/root/types/utilities'

const DEFAULT_OFFSET = 10

export type TTooltipOptions = {
  initialOpen?: boolean
  placement?: Placement
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * How far the tooltip should be from trigger element defaults to `DEFAULT_OFFSET`
   */
  offset?: number
  showArrow?: boolean
}

export function useTooltip(tooltipOptions: TTooltipOptions) {
  const {
    initialOpen,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    placement = 'top',
    offset: tooltipOffset,
    showArrow = true,
  } = tooltipOptions

  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen)

  const arrowRef = useRef<HTMLDivElement | null>(null)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const tooltipData = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(tooltipOffset ?? DEFAULT_OFFSET),
      flip(),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
  })

  const tooltipContext = tooltipData.context

  const hover = useHover(tooltipContext, {
    move: false,
    enabled: !controlledOpen,
  })

  const focus = useFocus(tooltipContext, {
    enabled: !controlledOpen,
  })

  const dismiss = useDismiss(tooltipContext)

  const role = useRole(tooltipContext, { role: 'tooltip' })

  const interactions = useInteractions([hover, focus, dismiss, role])

  return useMemo(
    () => ({
      open,
      setOpen,
      showArrow,
      arrowRef,
      ...interactions,
      ...tooltipData,
    }),
    [interactions, open, setOpen, showArrow, tooltipData],
  )
}

type ContextType = ReturnType<typeof useTooltip> | null

const TooltipContext = createContext<ContextType>(null)

export const useTooltipContext = () => {
  const context = useContext(TooltipContext)

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />')
  }

  return context
}

export function Tooltip(props: WithChildren & TTooltipOptions) {
  const { children, ...options } = props

  const tooltip = useTooltip(options)

  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  )
}

export const TooltipTrigger = forwardRef<
  HTMLElement,
  HTMLProps<HTMLElement> & { asChild?: boolean }
>(function Tooltiptrigger(props, propRef) {
  const { asChild, children, ...restProps } = props
  const tooltipContext = useTooltipContext()
  const { getReferenceProps, open } = tooltipContext

  const ref = useMergeRefs([tooltipContext.refs.setReference, propRef])

  const dataState = open ? 'open' : 'closed'

  // there is a bug where in if you use `asChild` tooltip doesn't get shown
  // need to fix it.
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      getReferenceProps({
        ref,
        ...restProps,
        ...children.props,
        'data-state': dataState,
      }),
    )
  }

  return (
    <button
      ref={node => {
        if (ref) {
          ref(node)
        }
      }}
      data-state={dataState}
      {...getReferenceProps(restProps)}
    >
      {children}
    </button>
  )
})

export const TooltipContent = forwardRef<HTMLElement, HTMLProps<HTMLElement>>(
  function TooltipContent(props, propRef) {
    const tooltipContextData = useTooltipContext()

    const {
      refs,
      open,
      strategy,
      y,
      x,
      getFloatingProps,
      arrowRef,
      middlewareData,
      placement,
    } = tooltipContextData

    const side = placement.split('-')[0] as Side

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[side]

    const ref = useMergeRefs([refs.setFloating, propRef])
    const { children } = props

    return (
      <FloatingPortal>
        {open && (
          <div
            ref={node => {
              if (ref) {
                ref(node)
              }
            }}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              visibility: x == null ? 'hidden' : 'visible',
              ...props.style,
              zIndex: 9999,
            }}
            {...getFloatingProps(props)}
          >
            {children}

            {tooltipContextData.showArrow && (
              <div
                ref={arrowRef}
                style={{
                  // top: middlewareData.arrow?.y
                  //   ? `${middlewareData.arrow.y}px`
                  //   : '',
                  left: middlewareData.arrow?.x
                    ? `${middlewareData.arrow.x}px`
                    : '',
                  // don't know what this magic number '25' but it seems to be working at the moment. If it isn't, please fix it or let Arihant know
                  [staticSide]: (arrowRef.current?.offsetWidth ?? 0) - 25,
                }}
                className={classNames(
                  'bg-ind-white border-grey-light pointer-events-none absolute h-4 w-4 rotate-45 transform',
                  {
                    'border-l-2 border-t-2': staticSide === 'top',
                    'border-b-2 border-r-2': staticSide === 'bottom',
                  },
                )}
              />
            )}
          </div>
        )}
      </FloatingPortal>
    )
  },
)
